import React, { ReactNode, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Button, ButtonProps } from './Button';
import { Screen } from '../Screen';
import { FaCross, FaTimes } from 'react-icons/fa';
import cx from 'classnames';

export type ModalAction = ButtonProps;

export interface ModalProps {
  title?: ReactNode | null;
  content?: ReactNode | null;
  children?: ReactNode | null;
  actions?: ButtonProps[] | null;
  width?: 'fullscreen' | 'small' | 'medium' | 'large' | 'xlarge';
  onClose: () => void;
  noContentPadding?: boolean;
  className?: string;
}

// Global modal stack management
interface ModalStackItem {
  id: string;
  onClose: () => void;
}

const ModalStackContext = React.createContext<{
  registerModal: (modal: ModalStackItem) => void;
  unregisterModal: (id: string) => void;
  isTopModal: (id: string) => boolean;
  getModalIndex: (id: string) => number;
}>({
  registerModal: () => {},
  unregisterModal: () => {},
  isTopModal: () => false,
  getModalIndex: () => -1,
});

export const ModalStackProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modalStack, setModalStack] = useState<ModalStackItem[]>([]);

  const registerModal = useCallback((modal: ModalStackItem) => {
    setModalStack(prev => [...prev, modal]);
  }, []);

  const unregisterModal = useCallback((id: string) => {
    setModalStack(prev => prev.filter(modal => modal.id !== id));
  }, []);

  const isTopModal = useCallback((id: string) => {
    return modalStack.length > 0 && modalStack[modalStack.length - 1].id === id;
  }, [modalStack]);

  const getModalIndex = useCallback((id: string) => {
    return modalStack.findIndex(modal => modal.id === id);
  }, [modalStack]);

  // Global escape key handler
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalStack.length > 0) {
        const topModal = modalStack[modalStack.length - 1];
        topModal.onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [modalStack]);

  return (
    <ModalStackContext.Provider value={{ registerModal, unregisterModal, isTopModal, getModalIndex }}>
      {children}
    </ModalStackContext.Provider>
  );
};

export const useModalStack = () => {
  return useContext(ModalStackContext);
};

export const ModalContext = React.createContext<{
  setTitle: (title: ReactNode) => void;
  setActions: (actions: ButtonProps[]) => void;
}>({
  setTitle: () => {},
  setActions: () => {},
});

export function useModalContext() {
  return useContext(ModalContext);
}

export function Modal({
  title,
  content,
  children,
  actions,
  onClose,
  width,
  noContentPadding,
  className,
}: ModalProps) {
  const [titleState, setTitle] = useState<ReactNode | null>(null);
  const [actionsState, setActions] = useState<ButtonProps[] | null>(null);
  const modalId = useRef(Math.random().toString(36).substr(2, 9));
  const { registerModal, unregisterModal, isTopModal, getModalIndex } = useModalStack();

  // Children state
  const modalContext = useMemo(() => {
    return {
      setTitle,
      setActions,
    }
  }, []);

  // Register/unregister modal in the stack
  useEffect(() => {
    registerModal({
      id: modalId.current,
      onClose,
    });

    return () => {
      unregisterModal(modalId.current);
    };
  }, [onClose, registerModal, unregisterModal]);

  // Modal width
  const widthClass = {
    fullscreen: 'w-full m-8 h-full',
    small: 'w-md',
    medium: 'w-3xl',
    large: 'w-6xl',
    xlarge: 'w-7xl',
  }[width || 'medium'];

  // Calculate z-index based on position in stack
  const modalIndex = getModalIndex(modalId.current);
  const zIndex = modalIndex >= 0 ? 1000 + modalIndex : 1000;

  return (
    <div
      className={cx(
        "fixed inset-0 bg-black/50 flex items-center justify-center",
        className
      )}
      style={{ zIndex }}
    >
      <div className={`bg-white rounded-lg ${widthClass} max-h-[calc(100vh-4rem)] flex flex-col relative`}>
        {/* Title */}
        {title && (
          <div className="text-xl font-bold p-3 border-b rounded-t-lg flex flex-row justify-start">
            <h2 className="flex-1">
              {titleState || title}
            </h2>

            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* No title bar, only close button */}
        {!title && (
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 absolute top-[-10px] right-[-10px] p-2 bg-white rounded-3xl z-10 border border-1 border-gray-400">
            <FaTimes className="w-6 h-6" />
          </button>
        )}
        
        <Screen>
          <ModalContext.Provider value={modalContext}>
            <div className={`grow overflow-y-auto ${noContentPadding ? '' : 'p-6'}`}>
              {content || children}
            </div>
          </ModalContext.Provider>
        </Screen>

        {(actions || actionsState) && <div className={`flex justify-end gap-2 mt-auto p-3 border-t`}>
          {(actionsState || actions)?.map((action, index) => (
            <Button
              key={index}
              {...action}
            />
          ))}
        </div>}
      </div>
    </div>
  );
}
